import {
  BurnId,
  BurnTemperatureSeries,
  BurnTemperatureSeriesImpl,
  flueBurnMetricAggregateTimeUnits,
} from "firefly-shared-ts-domain";
import React, { FC, useMemo } from "react";
import { TimeSeries, TimeSeriesImpl } from "firefly-shared-ts-timeseries";
import { alpha, SxProps, useTheme } from "@mui/material";
import {
  TimeAxisValueFormatterFactory,
  TimeSeriesMuiChart,
} from "firefly-shared-ts-timeseries-mui-chart";
import { Flue } from "firefly-my-chimney-ts-building";
import { ChartsReferenceLine, XAxis, YAxis } from "@mui/x-charts-premium";
import { useResolveMaxTemperatureReferenceLineProps } from "./useResolveMaxTemperatureReferenceLineProps";
import { DateTime, Duration } from "luxon";
import { MuiStructuralChartContainer } from "shared-ts-mui";
import { SizeClass } from "shared-ts-ui-responsive";
import { interpolateRdYlBu } from "d3-scale-chromatic";
import { useResolveAvgMaxTemperatureReferenceLineProps } from "./useResolveAvgMaxTemperatureReferenceLineProps";

const unit = "°C";

export interface BurnTimeSeriesSensitiveChartProps {
  flue: Flue;
  burnTimeSeries?: Array<BurnTemperatureSeries>;
  selected: Array<BurnId>;
  selectedMonth?: DateTime;
  timeAxis?: {
    maxDuration?: Duration | undefined;
  };
  hideMaxTemperatureReferenceLines?: boolean;
  hideAvgMaxTemperatureReferenceLines?: boolean;
  layout?: SizeClass;
  sx?: SxProps;
}

export const BurnTimeSeriesSensitiveChart: FC<
  BurnTimeSeriesSensitiveChartProps
> = (props) => {
  const { flue, burnTimeSeries, selected, selectedMonth } = props;
  const hideMaxTemperatureReferenceLines =
    props.hideMaxTemperatureReferenceLines ?? false;
  const hideAvgMaxTemperatureReferenceLines =
    props.hideAvgMaxTemperatureReferenceLines ?? false;
  const theme = useTheme();
  const flueMetricsSinceStart = flue.querySingleMetricsOrUndef(
    flueBurnMetricAggregateTimeUnits.ever
  );

  const maxTemperatureSinceStart = flueMetricsSinceStart?.values.temperatureMax;

  const maxY = useMemo(() => {
    return maxTemperatureSinceStart ?? 100;
  }, [maxTemperatureSinceStart]);

  const maxTemperatureReferenceLineProps =
    useResolveMaxTemperatureReferenceLineProps(flue, unit, selectedMonth);

  const avgMaxTemperatureReferenceLineProps =
    useResolveAvgMaxTemperatureReferenceLineProps(flue, unit, selectedMonth);

  const timeSeriesArray: Array<TimeSeries<number | null>> | undefined =
    useMemo(() => {
      return burnTimeSeries
        ?.filter((it) => selected.includes(it.burnId))
        .map((burnSeries) => {
          return new TimeSeriesImpl({
            info: {
              id: `${burnSeries.burnId}`,
              sid: flue.id,
              name: "",
              resolution: Duration.fromMillis(1000 * 60).toISO(),
              unit: unit,
            },
            points: burnSeries.dataPoints,
          });
        });
    }, [burnTimeSeries, selected, flue.id]);

  const yAxisConfig: Array<YAxis<"linear">> = useMemo(() => {
    const minY =
      burnTimeSeries !== undefined
        ? BurnTemperatureSeriesImpl.getMinY(burnTimeSeries, 0)
        : 0;

    return [
      {
        max: maxY,
        min: minY,
        colorMap: {
          type: "continuous",
          min: minY,
          max: maxY,
          color: (value) => interpolateRdYlBu(1 - (0.5 + 0.5 * value)),
        },
      } as YAxis<"linear">,
    ];
  }, [burnTimeSeries, maxY]);

  const xAxisMax = useMemo(() => {
    if (
      timeSeriesArray == null ||
      timeSeriesArray.length === 0 ||
      props.timeAxis?.maxDuration === undefined
    ) {
      return undefined;
    }

    if (timeSeriesArray.length > 1) {
      throw new Error("Viewing more than one time series is not supported yet");
    }

    return timeSeriesArray[0].firstPoint?.time.plus(
      props.timeAxis?.maxDuration
    );
  }, [props.timeAxis?.maxDuration, timeSeriesArray]);

  const ignitionEndTime = useMemo(() => {
    if (timeSeriesArray == null || timeSeriesArray.length === 0) {
      return undefined;
    }

    if (timeSeriesArray.length > 1) {
      throw new Error("Viewing more than one time series is not supported yet");
    }

    return timeSeriesArray[0].firstPoint?.time.plus({ minutes: 15 });
  }, [timeSeriesArray]);

  const xAxisConfig: XAxis<"time"> | undefined = useMemo(() => {
    return {
      max: xAxisMax?.toMillis(),
      valueFormatter: TimeAxisValueFormatterFactory.create({
        formatOptions: DateTime.TIME_24_SIMPLE,
      }),
      domainLimit: "strict",
    } as XAxis<"time">;
  }, [xAxisMax]);

  return (
    <MuiStructuralChartContainer sx={props.sx}>
      <TimeSeriesMuiChart
        timeSeries={timeSeriesArray ?? []}
        yAxisConfig={yAxisConfig}
        xAxisConfig={xAxisConfig}
        sx={{
          ".MuiLineElement-root": {
            strokeWidth: 3,
            filter: "drop-shadow(2px 3px 3px rgba(0, 0, 0, 0.9))",
          },
        }}
      >
        {ignitionEndTime != null && (
          <ChartsReferenceLine
            x={ignitionEndTime?.toMillis()}
            lineStyle={{
              stroke: alpha(theme.palette.divider, 0.2),
            }}
          />
        )}
        {!hideMaxTemperatureReferenceLines &&
          maxTemperatureReferenceLineProps.map((it) => {
            return <ChartsReferenceLine key={it.label} {...it} />;
          })}
        {!hideAvgMaxTemperatureReferenceLines &&
          avgMaxTemperatureReferenceLineProps.map((it) => {
            return <ChartsReferenceLine key={it.label} {...it} />;
          })}
      </TimeSeriesMuiChart>
    </MuiStructuralChartContainer>
  );
};
